import { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChatMessage, AIChatMode } from '../types/aiChat';
import { aiChatService } from '../services/aiChatService';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { roleContext } from '../context/role/context';

interface UserProfile {
    uid?: string | number;
    cid?: string | number;
    name?: string;
    user_name?: string;
    username?: string;
    role_id?: number;
    colaborador?: {
        role_id?: number;
    };
}

export const useAIChat = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { profile } = useContext(roleContext) as { profile?: UserProfile };
    const location = useLocation();

    // Clave de almacenamiento local aislada por usuario y empresa
    const storageKey = useMemo(() => {
        const uid = profile?.uid || 'guest';
        const cid = profile?.cid || 'default';
        return `easypos_ai_chat_${uid}_${cid}`;
    }, [profile?.uid, profile?.cid]);

    // Cargar historial persistido al iniciar o cambiar de usuario
    useEffect(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed)) {
                    setMessages(parsed.slice(-50));
                }
            } else {
                setMessages([]);
            }
        } catch {
            setMessages([]);
        }
    }, [storageKey]);

    // Persistir historial en localStorage cada vez que cambian los mensajes
    useEffect(() => {
        try {
            if (messages.length > 0) {
                localStorage.setItem(storageKey, JSON.stringify(messages.slice(-50)));
            }
        } catch {
            // Manejo silencioso en caso de limite de espacio
        }
    }, [messages, storageKey]);

    // Obtener y capitalizar el primer nombre del usuario
    const userName = useMemo(() => {
        const rawName = profile?.name || profile?.user_name || profile?.username || localStorage.getItem('user_name') || '';
        if (!rawName || !rawName.trim()) return '';
        const firstName = rawName.trim().split(/\s+/)[0];
        return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    }, [profile]);

    // Determinar modo automáticamente por rol
    const mode: AIChatMode = useMemo(() => {
        const roleId = profile?.colaborador ? profile.colaborador.role_id : profile?.role_id;
        return roleId === 4 ? 'accountant' : 'business';
    }, [profile]);

    const toggleOpen = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const closeChat = useCallback(() => {
        setIsOpen(false);
    }, []);

    const openChat = useCallback(() => {
        setIsOpen(true);
    }, []);

    // Formatear y mapear el nombre legible de la vista y subtab actual
    const currentView = useMemo(() => {
        const path = location.pathname.toLowerCase();
        const hash = location.hash.replace('#', '');
        
        let viewName = 'General / Inicio';

        if (path.includes('stock')) {
            viewName = 'Inventario de Productos';
            if (hash === 'tab-productos') viewName += ' (Productos)';
            else if (hash === 'tab-servicios') viewName += ' (Servicios)';
            else if (hash === 'tab-notas') viewName += ' (Notas de Inventario)';
        } else if (path.includes('pos')) {
            viewName = 'Punto de Venta (POS)';
        } else if (path.includes('compras')) {
            viewName = 'Compras y Gastos';
        } else if (path.includes('accounting')) {
            viewName = 'Módulo de Contabilidad';
        } else if (path.includes('accounts-management')) {
            viewName = 'Cuentas por Cobrar / Pagar';
        } else if (path.includes('terceros')) {
            viewName = 'CRM de Terceros / Clientes';
        } else if (path.includes('invoice')) {
            viewName = 'Facturación Electrónica';
        } else if (path.includes('profile')) {
            viewName = 'Perfil de Usuario';
        } else if (path.includes('operations')) {
            viewName = 'Gestión de Operaciones';
        }

        return viewName;
    }, [location.pathname, location.hash]);

    const sendMessage = async (text: string) => {
        if (!text.trim() || isLoading) return;

        const userMsg: ChatMessage = {
            role: 'user',
            content: text.trim(),
            timestamp: new Date().toISOString(),
        };

        const updatedMessages = [...messages, userMsg];
        setMessages(updatedMessages);
        setIsLoading(true);
        setError(null);

        try {
            const res = await aiChatService.sendMessage({
                messages: updatedMessages,
                ui_context: {
                    mode,
                    current_view: currentView,
                },
            });

            if (res.success && res.data?.response) {
                const assistantMsg: ChatMessage = {
                    role: 'assistant',
                    content: res.data.response,
                    sources: res.data.sources,
                    timestamp: new Date().toISOString(),
                };
                setMessages((prev) => [...prev, assistantMsg]);
            } else {
                const errorMessage = res.message || 'Error al comunicarse con la IA';
                setError(errorMessage);
            }
        } catch (err: unknown) {
            const errorObj = err as { response?: { data?: { message?: string } }; message?: string };
            const errMsg = errorObj?.response?.data?.message || errorObj?.message || 'Error inesperado de red';
            setError(errMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const clearMessages = useCallback(() => {
        setMessages([]);
        setError(null);
        try {
            localStorage.removeItem(storageKey);
        } catch {
            // Manejo silencioso de excepciones de almacenamiento
        }
    }, [storageKey]);

    return {
        isOpen,
        toggleOpen,
        openChat,
        closeChat,
        messages,
        isLoading,
        mode,
        currentView,
        userName,
        error,
        sendMessage,
        clearMessages,
    };
};
